import type { ModelMedia } from '@frameio/graphql-client/generated';
import type { TranscodeKeys } from '@frameio/shared/assets/types';
import type { DownloadableAsset } from './fetchAssetsForDownload';

/**
 * Resolves a proxy preference (highest or lowest) to an actual transcode for
 * video assets.
 *
 * For video assets:
 * - Filters available proxy transcodes (excluding original and invalid entries)
 * - Sorts by width and returns the highest or lowest resolution proxy
 * - Falls back to original if no valid proxies are available
 *
 * For non-video assets:
 * - Always returns the original transcode
 *
 * @param target - The asset to resolve a proxy for
 * @param transcodeKey - Either 'proxy-highest' or 'proxy-lowest'
 * @returns The selected transcode object, or null if unavailable
 */
function getLowestOrHighestTranscode(
  target: DownloadableAsset,
  transcodeKey: TranscodeKeys
) {
  const isProxyPreference =
    transcodeKey === 'proxy-highest' || transcodeKey === 'proxy-lowest';

  if (!target || !('media' in target) || !isProxyPreference) return null;

  if (
    target.__typename === 'VideoAsset' ||
    target.__typename === 'ModelAsset'
  ) {
    const videoTranscodes = target.media?.videoTranscodes;
    if (!videoTranscodes || videoTranscodes.length === 0) {
      return target.media?.original ?? null;
    }

    // Filter out original and null transcodes, only get proxy transcodes with
    // valid download URLs
    const proxyTranscodes = videoTranscodes.filter(
      (t) =>
        t &&
        t.key !== 'original' &&
        t.downloadUrl &&
        t.width !== null &&
        t.width !== undefined
    );

    if (proxyTranscodes.length === 0) {
      return target.media?.original ?? null;
    }

    // Sort by width to determine highest/lowest
    const sortedTranscodes = [...proxyTranscodes].sort((a, b) => {
      const widthA = a.width ?? 0;
      const widthB = b.width ?? 0;
      return transcodeKey === 'proxy-highest'
        ? widthB - widthA
        : widthA - widthB;
    });

    return sortedTranscodes[0] ?? null;
  }

  // For non-video assets, use original when proxy is requested
  return target.media?.original ?? null;
}

/**
 * Gets the transcode data for a given asset for the given trancode key.
 *
 * @param asset The asset to download
 * @param transcodeKey The key for the requested transcode
 * @returns Object The transcode metadata
 */
export function getTranscodeByKey(
  asset: DownloadableAsset,
  transcodeKey: TranscodeKeys
) {
  if (!asset) return null;

  const target =
    asset.__typename === 'VersionStackAsset' ? asset.versions?.[0] : asset;

  if (!target || !('media' in target)) return null;

  /**
   * For watermarked audio assets, if our original downloadUrl is null, and the
   * selected transcodeKey is original, we specifically look for the h264_720
   * transcode.
   */
  if (
    target &&
    target.__typename === 'AudioAsset' &&
    target.isWatermarked &&
    target.media?.original?.downloadUrl === null
  ) {
    if (target.media.audioTranscodes) {
      return (
        target.media.audioTranscodes.find((t) => t.key === 'h264_720') ?? null
      );
    }
  }

  /**
   * For watermarked document assets, if our original downloadUrl is null,
   * we need to generate a downloadUrl for the watermarked PDF version of the asset.
   * If that is null, as a fallback, we generate the PNG version of the first page
   * of the asset.
   */
  if (
    target?.__typename === 'DocumentAsset' &&
    target?.isWatermarked &&
    target?.media?.original?.downloadUrl === null
  ) {
    const baseUrl = target?.media?.metadata?.baseUrl;
    const assetNameWithoutExtension =
      target?.name?.substring(0, target?.name?.lastIndexOf('.')) ||
      target?.name;

    if (!baseUrl || !assetNameWithoutExtension) return null;

    // pdfDownload.url is already a complete, ready-to-use download URL (the
    // server bakes the filename into its own `download` param), so use it as-is
    // instead of appending a second, redundant `download` param.
    const pdfBaseUrl = target?.media?.pdfDownload?.url;
    if (pdfBaseUrl) {
      return { downloadUrl: pdfBaseUrl };
    }

    const downloadUrl = new URL(baseUrl);
    downloadUrl?.searchParams?.append('page', '0');
    downloadUrl?.searchParams?.append(
      'download',
      `${assetNameWithoutExtension}.png`
    );

    return { downloadUrl: downloadUrl?.toString() };
  }

  /**
   * For watermarked image assets, if our original downloadUrl is null,
   * we need to generate a downloadUrl for the image_full or image_small
   * version of the asset.
   */
  if (
    target?.__typename === 'ImageAsset' &&
    target?.isWatermarked &&
    target?.media?.original?.downloadUrl === null
  ) {
    if (target?.media?.imageTranscodes) {
      return (
        target?.media?.imageTranscodes?.find((t) => t.key === 'image_full') ||
        target?.media?.imageTranscodes?.find((t) => t.key === 'image_small') ||
        null
      );
    }
  }

  /**
   * For watermarked video assets, if our original downloadUrl is null, and the
   * selected transcodeKey is original, we need to use downloadUrl for
   * highest available resolution
   */
  if (
    (target?.__typename === 'VideoAsset' ||
      target?.__typename === 'ModelAsset') &&
    (target?.isWatermarked || target?.forensicallyWatermarked) &&
    transcodeKey === 'original' &&
    target?.media?.original?.downloadUrl === null
  ) {
    if (target?.media?.videoTranscodes) {
      return (
        target?.media?.videoTranscodes?.find((t) => t.downloadUrl !== null) ||
        null
      );
    }
  }

  /**
   * Used in Premiere integration when importing mutliple assets or folders.
   * Allows for importing original asset, highest resolution proxy, or lowest
   * resolution proxy.
   */
  if (transcodeKey === 'proxy-highest' || transcodeKey === 'proxy-lowest') {
    return getLowestOrHighestTranscode(target, transcodeKey);
  }

  if (transcodeKey === 'original') {
    return target.media?.original ?? null;
  }

  if (
    target.__typename === 'ModelAsset' &&
    transcodeKey === 'usdz_conversion'
  ) {
    return (
      (target.media as ModelMedia)?.modelTranscodes?.find(
        (t) => t.key === transcodeKey
      ) ?? null
    );
  }

  if (
    target.__typename === 'VideoAsset' ||
    target.__typename === 'ModelAsset'
  ) {
    const transcode = target.media?.videoTranscodes?.find(
      (t) => t.key === transcodeKey
    );

    return transcode ? transcode ?? null : null;
  }

  if (target.__typename === 'AudioAsset') {
    const transcode = target.media?.audioTranscodes?.find(
      (t) => t.key === transcodeKey
    );

    return transcode ? transcode ?? null : null;
  }

  if (target.__typename === 'ImageAsset') {
    const transcode = target.media?.imageTranscodes?.find(
      (t) => t.key === transcodeKey
    );

    return transcode ? transcode ?? null : null;
  }

  if (target.__typename === 'InteractiveAsset') {
    return target.media?.original ?? null;
  }

  return null;
}
